import React, { useState } from "react";
import { FaSearch, FaTimesCircle } from "react-icons/fa";
import {Helmet} from "react-helmet";

export default function RegexTester() {
    const [regex, setRegex] = useState("");
    const [testString, setTestString] = useState("");
    const [matches, setMatches] = useState([]);
    const [error, setError] = useState("");

    const handleTest = () => {
        try {
            const pattern = new RegExp(regex, "g");
            const foundMatches = testString.match(pattern) || [];
            setMatches(foundMatches);
            setError("");
        } catch (err) {
            setError("Invalid Regular Expression");
            setMatches([]);
        }
    };

    return (
        <>

            <Helmet>
                <title>Regex Tester Tool - Test Your Regular Expressions | WebUtility</title>
                <meta
                    name="description"
                    content="Test your regular expressions in real-time with this online Regex tester tool. Get immediate feedback and match results for your regex patterns and test strings."
                />
                <meta
                    name="keywords"
                    content="Regex tester, online regex tool, test regular expressions, regular expressions matcher, regex pattern tester"
                />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href="https://webutility.online/regex-tester" />
                <meta property="og:title" content="Regex Tester Tool - Test Your Regular Expressions | WebUtility" />
                <meta
                    property="og:description"
                    content="Test your regular expressions in real-time with this online Regex tester tool. Get immediate feedback and match results for your regex patterns and test strings."
                />
                <meta property="og:url" content="https://webutility.online/regex-tester" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://webutility.online/assets/regex-tester-preview.png" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Regex Tester Tool - Test Your Regular Expressions | WebUtility" />
                <meta
                    name="twitter:description"
                    content="Test your regular expressions in real-time with this online Regex tester tool. Get immediate feedback and match results for your regex patterns and test strings."
                />
                <meta name="twitter:image" content="https://webutility.online/assets/regex-tester-preview.png" />
            </Helmet>
            <main className="flex-grow px-4 py-8 ">
                <div className="container mx-auto p-6 max-w-screen-2xl border  rounded-md shadow-lg">
                    {/* Heading */}
                    <div className="text-center mb-6">
                        <h2 className="text-3xl font-bold text-gray-800">Regex Tester</h2>
                        <p className="text-gray-600">Test your regular expressions in real-time.</p>
                    </div>

                    {/* Regex Input */}
                    <div className="mb-4">
                        <label className="block text-gray-700 font-semibold mb-2">Enter Regex Pattern:</label>
                        <input
                            type="text"
                            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., \d{3}"
                            value={regex}
                            onChange={(e) => setRegex(e.target.value)}
                        />
                    </div>

                    {/* Test String Input */}
                    <div className="mb-4">
                        <label className="block text-gray-700 font-semibold mb-2">Test String:</label>
                        <textarea
                            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            rows="4"
                            placeholder="Enter text to test your regex..."
                            value={testString}
                            onChange={(e) => setTestString(e.target.value)}
                        ></textarea>
                    </div>

                    {/* Test Button */}
                    <button
                        onClick={handleTest}
                        className="flex items-center justify-center bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition w-full"
                    >
                        <FaSearch className="mr-2" /> Test Regex
                    </button>

                    {/* Error Message */}
                    {error && (
                        <p className="text-red-500 mt-3 flex items-center">
                            <FaTimesCircle className="mr-2" /> {error}
                        </p>
                    )}

                    {/* Match Results */}
                    <div className="mt-6">
                        <h3 className="text-lg font-semibold text-gray-800">Matches:</h3>
                        {matches.length > 0 ? (
                            <ul className="mt-2 bg-gray-100 p-4 rounded-lg">
                                {matches.map((match, index) => (
                                    <li key={index} className="text-blue-600 font-mono">{match}</li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-gray-500 mt-2">No matches found.</p>
                        )}
                    </div>
                </div>

                <div className="container mx-auto p-6 max-w-screen-2xl bg-white shadow-lg rounded-lg mt-2">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Regex Tester Guide</h2>
            <p className="text-gray-600 mb-4">
                Welcome to the <strong>Regex Tester Tool</strong>! This tool allows you to test regular expressions in real-time and get immediate feedback on how your regex patterns match against any test string. Follow this simple guide to get started:
            </p>

            {/* Step 1 */}
            <div className="mb-6">
                <h3 className="text-2xl font-semibold text-gray-800 mb-2">1. Enter Regex Pattern</h3>
                <p className="text-gray-600">
                    In the <strong>"Enter Regex Pattern"</strong> field, type your regular expression. For example, to match a 3-digit number, enter <code>\d{3}</code> (which matches any sequence of 3 digits). If you're unsure about the pattern to use, check the regular expression documentation or try some sample patterns available online.
                </p>
            </div>

            {/* Step 2 */}
            <div className="mb-6">
                <h3 className="text-2xl font-semibold text-gray-800 mb-2">2. Enter Test String</h3>
                <p className="text-gray-600">
                    In the <strong>"Test String"</strong> text area, enter the string of text you want to test your regular expression against. The tool will check the test string for any matches based on the regex pattern you entered.
                </p>
            </div>

            {/* Step 3 */}
            <div className="mb-6">
                <h3 className="text-2xl font-semibold text-gray-800 mb-2">3. Run the Test</h3>
                <p className="text-gray-600">
                    After entering your regex and test string, click the <strong>"Test Regex"</strong> button. The tool will apply your regex pattern to the test string and show you any matching results. If the pattern is invalid or there's an error, the tool will display an error message to help you troubleshoot.
                </p>
            </div>

            {/* Step 4 */}
            <div className="mb-6">
                <h3 className="text-2xl font-semibold text-gray-800 mb-2">4. View Match Results</h3>
                <p className="text-gray-600">
                    Once the test is complete, you’ll see the matches below in the <strong>"Matches"</strong> section. If there are any matches, they will be listed as separate items. If no matches are found, you’ll see a message saying <strong>"No matches found."</strong>
                </p>
            </div>

            {/* Step 5 */}
            <div className="mb-6">
                <h3 className="text-2xl font-semibold text-gray-800 mb-2">5. Handle Errors</h3>
                <p className="text-gray-600">
                    If you input an invalid regular expression (e.g., a syntax error), an error message will appear below the test button. The message will tell you the error type, so you can fix the issue. For example, you might have missed closing a parenthesis or made a typo in your pattern.
                </p>
            </div>

            {/* Helpful Tips */}
            <div className="mb-6">
                <h3 className="text-2xl font-semibold text-gray-800 mb-2">Helpful Tips</h3>
                <ul className="list-disc pl-6 text-gray-600">
                    <li><strong>Regex Syntax</strong>: Regular expressions are a powerful way to match patterns in text. For example:
                        <ul className="list-inside">
                            <li><code>\d</code> matches any digit.</li>
                            <li><code>\w</code> matches any word character (letters, digits, and underscores).</li>
                            <li><code>\s</code> matches any whitespace character.</li>
                            <li><code>{`{n}`}</code> specifies that the previous character or group should appear exactly <strong>n</strong> times.</li>
                        </ul>
                    </li>
                    <li><strong>Flags</strong>: This tool supports the <code>g</code> flag (global search) to find all matches in the test string. You can add other flags like <code>i</code> (ignore case) or <code>m</code> (multi-line) by adjusting your regular expression.</li>
                    <li><strong>Experiment</strong>: Don’t hesitate to try different regular expressions to see how they behave with various test strings!</li>
                </ul>
            </div>

            {/* Troubleshooting */}
            <div className="mb-6">
                <h3 className="text-2xl font-semibold text-gray-800 mb-2">Troubleshooting</h3>
                <p className="text-gray-600">
                    If the tool is not returning the expected results:
                </p>
                <ul className="list-disc pl-6 text-gray-600">
                    <li>Check for typos in your regular expression.</li>
                    <li>Ensure that your test string contains the content you're expecting.</li>
                    <li>Try using simpler patterns first to ensure the tool is working correctly before building more complex ones.</li>
                </ul>
            </div>

            {/* Example Use Cases */}
            <div className="mb-6">
                <h3 className="text-2xl font-semibold text-gray-800 mb-2">Example Use Cases</h3>
                <ul className="list-disc pl-6 text-gray-600">
                    <li><strong>Email Validation</strong>: <code>^[\w-\.]+@([\w-]+\.)+[a-zA-Z]{"2,7"}$</code> – This regex pattern can be used to match a valid email address.</li>
                    <li><strong>Phone Number</strong>: <code>^\+?[1-9]\d{"1,14"}$</code> – This pattern matches international phone numbers.</li>
                </ul>
            </div>
        </div>
            </main>
        </>


    );
}
